import type {
	DiscriminatorSpec,
	DiscriminatorSpecFields,
	TaxonomyClass,
	TaxonomyClassFields,
	TaxonomyDraftIssue,
} from "@sitescene/types";

import type { TaxonomyWorkspace } from "@/features/plausibility/use-taxonomy-editor";

export interface ClassRow {
	classId: string;
	fields: TaxonomyClassFields;
	issues: TaxonomyDraftIssue[];
	/** The live class, absent for one the draft creates. */
	live?: TaxonomyClass;
	/**
	 * Feature keys past verifications may refer to: the live spec's, or the
	 * seed's when the verifier has been reading that. Locked in the editor.
	 */
	lockedKeys: string[];
	spec: DiscriminatorSpecFields | null;
	specSource: SpecSource;
	specState: DraftState;
	state: DraftState;
}

export type DraftState = "changed" | "live" | "new";

/** Where a class's features come from, as the verifier would resolve them. */
export type SpecSource = "draft" | "inactive" | "live" | "missing" | "seed";

export const EMPTY_CLASS: TaxonomyClassFields = {
	active: true,
	alertable: false,
	confusers: [],
	costs: { falseNegative: 1, falsePositive: 1 },
	defaultLogPriorOdds: 0,
	displayName: "",
};

export const EMPTY_SPEC: DiscriminatorSpecFields = {
	active: true,
	confusers: [],
	features: [],
};

/** Field names a draft entry changes relative to its base, for the review. */
export function changedFields<T extends object>(
	base: null | T,
	value: T,
): string[] {
	if (!base) return ["new"];
	const keys = new Set([...Object.keys(base), ...Object.keys(value)]);
	return [...keys]
		.filter(
			(key) =>
				JSON.stringify(base[key as keyof T]) !==
				JSON.stringify(value[key as keyof T]),
		)
		.sort();
}

export function classFields(cls: TaxonomyClass): TaxonomyClassFields {
	return {
		active: cls.active,
		alertable: cls.alertable,
		confusers: cls.confusers ?? [],
		costs: cls.costs,
		defaultLogPriorOdds: cls.defaultLogPriorOdds,
		...(cls.description && { description: cls.description }),
		displayName: cls.displayName,
		...(cls.parent && { parent: cls.parent }),
	};
}

/**
 * The taxonomy as it would look with the draft published: every live class,
 * every class the draft adds, each with the draft's value where it has one
 * and the spec the verifier would use.
 */
export function classRows(workspace: TaxonomyWorkspace): ClassRow[] {
	const { classes, draft, issues, seedSpecs, specs } = workspace;
	const liveById = new Map(classes.map((cls) => [cls.classId, cls]));
	const liveSpecs = new Map(
		specs
			.filter((spec) => spec.scopeType === "global")
			.map((spec) => [spec.classId, spec]),
	);
	const draftClasses = new Map(
		(draft?.classes ?? []).map((change) => [change.classId, change]),
	);
	const draftSpecs = new Map(
		(draft?.specs ?? []).map((change) => [change.classId, change]),
	);
	const seedById = new Map(
		seedSpecs.map((seed) => [seed.classId, seed.fields]),
	);

	const ids = [...new Set([...draftClasses.keys(), ...liveById.keys()])].sort();

	return ids.map((classId) => {
		const live = liveById.get(classId);
		const change = draftClasses.get(classId);
		const specChange = draftSpecs.get(classId);
		const liveSpec = liveSpecs.get(classId);

		const spec = specChange
			? specChange.value
			: liveSpec
				? specFields(liveSpec)
				: (seedById.get(classId) ?? null);
		const specSource: SpecSource = specChange
			? "draft"
			: liveSpec
				? liveSpec.active
					? "live"
					: "inactive"
				: seedById.has(classId)
					? "seed"
					: "missing";

		const referenced = liveSpec ? specFields(liveSpec) : seedById.get(classId);
		return {
			classId,
			fields: change ? change.value : classFields(live!),
			issues: issues.filter(
				(issue) =>
					issue.target === classId ||
					issue.target.startsWith(`${classId} spec`),
			),
			lockedKeys: referenced?.features.map((feature) => feature.key) ?? [],
			...(live && { live }),
			spec,
			specSource,
			specState: specChange ? (specChange.base ? "changed" : "new") : "live",
			state: change ? (change.base ? "changed" : "new") : "live",
		};
	});
}

/**
 * Alertable classes that name this one as a look-alike, on the class or in
 * its features. Where the cues that tell this class apart belong.
 */
export function lookAlikeOf(
	rows: readonly ClassRow[],
	classId: string,
): ClassRow[] {
	return rows.filter(
		(row) =>
			row.classId !== classId &&
			row.fields.alertable &&
			(row.fields.confusers.includes(classId) ||
				(row.spec?.confusers.includes(classId) ?? false)),
	);
}

export function specFields(
	spec: Pick<DiscriminatorSpec, "active" | "confusers" | "features">,
): DiscriminatorSpecFields {
	return {
		active: spec.active,
		confusers: spec.confusers ?? [],
		features: spec.features ?? [],
	};
}

/** "Spec (seed)" and friends, for the class list. */
export const SPEC_SOURCE_LABELS: Record<SpecSource, string> = {
	draft: "features in draft",
	inactive: "spec inactive",
	live: "features",
	missing: "no features",
	seed: "seed features",
};

/** Lowercase kebab-case, as the anchored response schema needs. */
export const FEATURE_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `family.member`, lowercase kebab-case. */
export const CLASS_ID_PATTERN = /^[a-z][a-z0-9-]*\.[a-z0-9][a-z0-9-]*$/;
